<script setup lang="ts">
import ClientContractsSection from '@/components/clients/delivery/ClientContractsSection.vue';
import ClientDeliveryMetrics from '@/components/clients/delivery/ClientDeliveryMetrics.vue';
import ClientExecutionSection from '@/components/clients/delivery/ClientExecutionSection.vue';
import ClientFilesSection from '@/components/clients/delivery/ClientFilesSection.vue';
import ClientProjectsSection from '@/components/clients/delivery/ClientProjectsSection.vue';
import ClientProjectDialogs from '@/components/clients/delivery/ClientProjectDialogs.vue';
import type { Approval, Project, ProjectContract, Proposal, Task, UploadedFile } from '~/types/models';
import type {
    ClientDeliveryContractItem,
    ClientDeliveryUploadScopeOption,
    ClientProjectCardStats,
    ClientProjectLinkItem,
    ClientTaskDraft,
} from './types';

interface Props {
    relatedProjects: Project[];
    pendingTasks: Task[];
    pendingApprovals: Approval[];
    clientContracts: ClientDeliveryContractItem[];
    clientProposals: Proposal[];
    scopedFiles: UploadedFile[];
    projectLinkItems: ClientProjectLinkItem[];
    projectStatsById: Record<string, ClientProjectCardStats | undefined>;
    canMutate: boolean;
    tenantSlug: string;
    linkedClientId?: string;
    uploadScope: string;
    uploadScopeOptions: ClientDeliveryUploadScopeOption[];
    selectedFilesCount: number;
    deliveryUploading: boolean;
    deliveryUploadError: string;
    galleryFilesAcrossProjects: UploadedFile[];
    filesLoading: boolean;
    tasksHref: string;
    approvalsHref: string;
    canCreateContract: boolean;
    clientContract?: ProjectContract | null;
    relatedProjectNameById: (projectId?: string | null) => string;
    createTask: (draft: ClientTaskDraft) => Promise<boolean>;
    publishProjectOpen: boolean;
    noticeProjectOpen: boolean;
    publishTargetProject?: Project | null;
    noticeTargetProject?: Project | null;
}

withDefaults(defineProps<Props>(), {
    linkedClientId: '',
    clientContract: null,
});

const emit = defineEmits<{
    (e: 'update:uploadScope', value: string): void,
    (e: 'files-selected', files: File[]): void,
    (e: 'upload-files'): void,
    (e: 'refresh-files'): void,
    (e: 'edit-project', project: Project): void,
    (e: 'notice-project', project: Project): void,
    (e: 'publish-project', project: Project): void,
    (e: 'archive-project', project: Project): void,
    (e: 'delete-project', project: Project): void,
    (e: 'open-task', taskId: string): void,
    (e: 'open-approval', approval: Approval): void,
    (e: 'create-contract'): void,
    (e: 'manage-client-contract'): void,
    (e: 'download-client-contract-pdf'): void,
    (e: 'update:publishProjectOpen', value: boolean): void,
    (e: 'update:noticeProjectOpen', value: boolean): void,
    (e: 'publish-project-saved'): void,
    (e: 'notice-project-saved'): void,
}>();
</script>

<template>
    <div class="space-y-6">
        <ClientDeliveryMetrics
            :project-count="relatedProjects.length"
            :open-work-count="pendingTasks.length + pendingApprovals.length"
            :agreement-count="clientContracts.length + clientProposals.length"
            :file-count="scopedFiles.length"
            @projects="document.getElementById('client-projects')?.scrollIntoView({ behavior: 'smooth' })"
            @work="document.getElementById('client-open-work')?.scrollIntoView({ behavior: 'smooth' })"
            @contracts="document.getElementById('client-contracts')?.scrollIntoView({ behavior: 'smooth' })"
            @files="document.getElementById('client-files')?.scrollIntoView({ behavior: 'smooth' })"
        />

        <div class="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(340px,0.9fr)]">
            <div class="space-y-4">
                <ClientProjectsSection id="client-projects"
                    :related-projects="relatedProjects"
                    :project-link-items="projectLinkItems"
                    :project-stats-by-id="projectStatsById"
                    :can-mutate="canMutate"
                    :tenant-slug="tenantSlug"
                    :linked-client-id="linkedClientId"
                    @edit="emit('edit-project', $event)"
                    @notice="emit('notice-project', $event)"
                    @publish="emit('publish-project', $event)"
                    @archive="emit('archive-project', $event)"
                    @delete="emit('delete-project', $event)"
                />

                <ClientFilesSection id="client-files"
                    :upload-scope="uploadScope"
                    :upload-scope-options="uploadScopeOptions"
                    :selected-files-count="selectedFilesCount"
                    :delivery-uploading="deliveryUploading"
                    :delivery-upload-error="deliveryUploadError"
                    :gallery-files-across-projects="galleryFilesAcrossProjects"
                    :files-loading="filesLoading"
                    :scoped-files="scopedFiles"
                    :can-mutate="canMutate"
                    :project-options="relatedProjects.map(project => ({ value: project.id, label: project.name || 'Untitled project' }))"
                    @update:upload-scope="emit('update:uploadScope', $event)"
                    @files-selected="emit('files-selected', $event)"
                    @upload-files="emit('upload-files')"
                    @refresh-files="emit('refresh-files')"
                />
            </div>

            <div class="space-y-4">
                <ClientExecutionSection id="client-open-work"
                    :can-mutate="canMutate"
                    :pending-tasks="pendingTasks"
                    :pending-approvals="pendingApprovals"
                    :related-projects="relatedProjects"
                    :tasks-href="tasksHref"
                    :approvals-href="approvalsHref"
                    :related-project-name-by-id="relatedProjectNameById"
                    :create-task="createTask"
                @open-task="emit('open-task', $event)"
                @open-approval="emit('open-approval', $event)"
                />

                <ClientContractsSection id="client-contracts"
                    :can-mutate="canMutate"
                    :can-create-contract="canCreateContract"
                    :client-contracts="clientContracts"
                    :client-proposals="clientProposals"
                    :client-id="linkedClientId"
                    :client-contract="clientContract"
                    @create-contract="emit('create-contract')"
                    @manage-client-contract="emit('manage-client-contract')"
                    @download-client-contract-pdf="emit('download-client-contract-pdf')"
                />
            </div>
        </div>

        <ClientProjectDialogs
            :publish-open="publishProjectOpen"
            :notice-open="noticeProjectOpen"
            :publish-target-project="publishTargetProject || null"
            :notice-target-project="noticeTargetProject || null"
            @update:publish-open="emit('update:publishProjectOpen', $event)"
            @update:notice-open="emit('update:noticeProjectOpen', $event)"
            @publish-saved="emit('publish-project-saved')"
            @notice-saved="emit('notice-project-saved')"
        />
    </div>
</template>
